import { CircularProgress, styled, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectConversations } from '../../redux/reducers/conversationReducer';
import { formatTokens } from '../../util/formatterUtils';
import LearnMore from '../LearnMore';

const WarningContainer = styled('div')({
  padding: '0 5px 0 5px',
  display: 'flex',
  justifyContent: 'space-between',
  whiteSpace: 'nowrap',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
  },
});

const TokenTrackerContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  '@media (max-width: 600px)': {
    order: 1,
    marginTop: '5px',
    marginBottom: '-5px',
    marginLeft: '-20px',
  },
});

const colorValue = (value = 0) => {
  switch (true) {
    case value <= 50:
      return `#00A3FF`;

    case value > 50 && value <= 75:
      return `#AF01FF`;

    case value > 75:
      return `#FF3D00`;
  }
};

const StyledCircularProgress = styled(CircularProgress)(({ value }) => ({
  color: `${colorValue(value)}`,
  strokeWidth: '4',
  transition: 'color 1s ease-in-out',
  marginTop: '5px',
}));

const LimitWarning = () => {
  const { currentNumTokens, maxNumTokens } = useSelector(selectConversations);

  const [tokenLimitWarningShown, setTokenLimitWarningShown] = useState(false);
  const [tokenLimitExceededShown, setTokenLimitExceededShown] = useState(false);

  // Safeguard against undefined values
  const safeCurrentTokens = currentNumTokens ?? 0;
  const safeMaxTokens = maxNumTokens ?? 1; // Avoid division by zero

  const percentage = safeCurrentTokens / safeMaxTokens;

  const progressValue = percentage >= 1 ? 1 : percentage;

  const warningMessage = useCallback(
    (isTooltip = false) => {
      if (percentage <= 0.75) {
        return '';
      }

      return (
        <span>
          <b>Conversation Limit Reached Soon</b>
          <br />
          This chat is getting a bit full! To help the AI stay accurate and on track, try
          summarizing earlier details or begin a new conversation.
          <LearnMore
            link="https://howto.westmonroe.com/a/2000020-understanding-context-windows-tokens-in-nigel"
            color="inherit"
            sx={{
              mr: '5px',
              mt: isTooltip ? '2px' : '4px',
              fontSize: isTooltip ? '11px' : '14px',
            }}
            iconSx={{
              mr: '5px',
              mt: isTooltip ? '2px' : '4px',
              fontSize: isTooltip ? '11px' : '14px',
            }}
          />
        </span>
      );
    },
    [percentage]
  );

  const snackbarMessage = useMemo(() => warningMessage(), [warningMessage]);

  useEffect(() => {
    // Skip if we don't have valid token data or if percentage is not a valid number
    if (
      !Number.isFinite(percentage) ||
      percentage <= 0.75 ||
      (tokenLimitWarningShown && tokenLimitExceededShown)
    ) {
      return;
    }

    let variant: 'warning' | 'error' = 'warning';

    if (!tokenLimitWarningShown && percentage > 0.75 && percentage < 1) {
      setTokenLimitWarningShown(true);
    } else if (!tokenLimitExceededShown && percentage >= 1) {
      variant = 'error';
      setTokenLimitExceededShown(true);
    }

    enqueueSnackbar(snackbarMessage, {
      variant,
      style: { maxWidth: '500px', flexFlow: 'row' },
    });
  }, [percentage, tokenLimitWarningShown, tokenLimitExceededShown, snackbarMessage]);

  return (
    <WarningContainer>
      <Tooltip placement="top" title={warningMessage(true)}>
        <TokenTrackerContainer>
          <StyledCircularProgress
            variant="determinate"
            aria-label={'Token Limit'}
            value={progressValue * 100}
            style={{ height: 20, width: 20 }}
          />
          <Typography variant="body2" sx={{ marginTop: '6px', color: '#50658E', fontSize: '12px' }}>
            {formatTokens(safeCurrentTokens)} of {formatTokens(safeMaxTokens)} tokens
          </Typography>
        </TokenTrackerContainer>
      </Tooltip>
    </WarningContainer>
  );
};

export default LimitWarning;
